import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IconType } from 'react-icons';
import { FiEye } from 'react-icons/fi';
import { FaShoppingCart, FaMinus, FaPlus } from 'react-icons/fa';
import { useConsultation } from '../../context/ConsultationContext';
import { useAuth } from '../../context/AuthContext';
import { Product } from '../../types';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../config';

const Card = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  
  
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const Badge = styled.span<{ type: 'featured' }>`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  background: ${props => props.type === 'featured' ? '#FDBF37' : '#3498db'};
  z-index: 1;
`;

const ActionButtons = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${Card}:hover & {
    opacity: 1;
  }
  
  /* Clase para optimización móvil en scroll horizontal */
  &.action-buttons {
    opacity: 1; /* Siempre visible en móvil */
  }
`;

const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.9);
  color: #2c3e50;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  & > svg {
    width: 1em;
    height: 1em;
  }

  &:hover {
    background: #3498db;
    color: white;
    transform: scale(1.1);
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  
  /* Clase para optimización móvil en scroll horizontal */
  &.card-content {
    padding: 0.75rem;
  }
`;

const ProductName = styled.h3`
  color: #2c3e50;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  line-height: 1.3;
  
  /* Clase para optimización móvil en scroll horizontal */
  &.product-name {
    font-size: 0.85rem;
    line-height: 1.2;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const ProductDescription = styled.p`
  color: #7f8c8d;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 1rem;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  /* Clase para optimización móvil en scroll horizontal */
  &.product-description {
    font-size: 0.75rem;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const ProductCategory = styled.span`
  color: #3498db;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1rem;
`;

const ProductSpecs = styled.div`
  margin-bottom: 1rem;
`;

const SpecItem = styled.span`
  display: inline-block;
  background: #ecf0f1;
  color: #2c3e50;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
`;

const ProductPrice = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e3a8a;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;

  .currency {
    font-size: 0.95rem;
    font-weight: 600;
    color: #4b5563;
  }

  .amount {
    font-size: 1.3rem;
    color: #1e3a8a;
  }

  /* Clase para optimización móvil */
  &.product-price {
    margin-bottom: 0.5rem;
    .amount {
      font-size: 1.1rem;
    }
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const ConsultButton = styled.button`
  background: #FDBF37;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(39, 174, 96, 0.3);
  }

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Clase para optimización móvil en scroll horizontal */
  &.consult-button {
    font-size: 0.8rem;
    padding: 0.5rem 1rem;
  }
`;

const StockInfo = styled.span<{ inStock: boolean }>`
  font-size: 0.8rem;
  color: ${props => props.inStock ? '#27ae60' : '#e74c3c'};
  font-weight: 600;
`;

interface ProductCardProps {
  product: Product;
  hideStockInfo?: boolean;
}

interface ConsultationProduct extends Product {
  quantity: number;
}

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  /* Clase para optimización móvil en scroll horizontal */
  &.quantity-controls {
    margin-bottom: 0.5rem;
  }
`;

const QuantityButton = styled.button`
  background: #f0f0f0;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const QuantityDisplay = styled.span`
  font-size: 1rem;
  min-width: 24px;
  text-align: center;
`;

interface IconProps {
  size?: number;
}

const Icon = ({ icon: IconComponent, size }: { icon: IconType; size?: number }) => {
  const Component = IconComponent as React.ComponentType<IconProps>;
  return <Component size={size} />;
};

const ProductCard: React.FC<ProductCardProps> = ({ product, hideStockInfo }) => {
  const navigate = useNavigate();
  const { addToConsultation } = useConsultation();
  const { isAuthenticated } = useAuth();
  const [quantity, setQuantity] = useState(1);

  const handleQuantityChange = (delta: number) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1) { // Remove stock limit restriction
      setQuantity(newQuantity);
    }
  };

  const handleAddToConsultation = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (Number(product.stock || 0) <= 0) {
      toast.error('Este producto está agotado');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para consultar productos');
      navigate('/login');
      return;
    }

    addToConsultation(product, quantity);
    setQuantity(1);
  };

  return (
    <Card onClick={() => navigate(`/producto/${product.id}`)}>
      <ImageContainer className="product-image">
        <ProductImage src={getImageUrl(product.image_url)} alt={product.name} />
        
        <ActionButtons className="action-buttons">
          <ActionButton 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/producto/${product.id}`);
            }} 
            title="Ver detalles"
          >
            <Icon icon={FiEye} size={16} />
          </ActionButton>
        </ActionButtons>
      </ImageContainer>

      <CardContent className="card-content">
        <ProductCategory>{product.category_name}</ProductCategory>
        <ProductName className="product-name">{product.name}</ProductName>
        <ProductDescription className="product-description">{product.description}</ProductDescription>
        
        {(product.brand || product.model) && (
          <ProductSpecs>
            {product.brand && <SpecItem>Marca: {product.brand}</SpecItem>}
            {product.model && <SpecItem>Modelo: {product.model}</SpecItem>}
          </ProductSpecs>
        )}
        
        <ProductPrice className="product-price">
          {product.use_offer_price && Number(product.offer_price || 0) > 0 ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
              <del style={{ color: "#94a3b8", fontSize: "0.95rem", fontWeight: 500 }}>
                S/. {Number(product.price || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </del>
              <div style={{ display: "flex", alignItems: "baseline", gap: "0.2rem" }}>
                <span className="currency" style={{ color: "#ea580c" }}>S/.</span>
                <span className="amount" style={{ color: "#ea580c", fontWeight: 800 }}>
                  {Number(product.offer_price).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          ) : (
            <>
              <span className="currency">S/.</span>
              <span className="amount">
                {Number(product.price || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </>
          )}
        </ProductPrice>

        <QuantityControls className="quantity-controls" onClick={(e) => e.stopPropagation()}>
          <QuantityButton 
            onClick={(e) => {
              e.stopPropagation();
              handleQuantityChange(-1);
            }}
            disabled={Number(product.stock || 0) <= 0}
          >
            <Icon icon={FaMinus} size={12} />
          </QuantityButton>
          <QuantityDisplay>{quantity}</QuantityDisplay>
          <QuantityButton 
            onClick={(e) => {
              e.stopPropagation();
              handleQuantityChange(1);
            }}
            disabled={Number(product.stock || 0) <= 0}
          >
            <Icon icon={FaPlus} size={12} />
          </QuantityButton>
        </QuantityControls>

        <CardFooter>
          {Number(product.stock || 0) > 0 ? (
            <ConsultButton
              className="consult-button"
              onClick={handleAddToConsultation}
            >
              <Icon icon={FaShoppingCart} size={16} />
              Agregar al carrito
            </ConsultButton>
          ) : (
            <ConsultButton
              className="consult-button"
              disabled={true}
              style={{
                backgroundColor: "#9ca3af",
                cursor: "not-allowed",
                boxShadow: "none"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              🚫 Agotado
            </ConsultButton>
          )}
        </CardFooter>
      </CardContent>
    </Card>
  );
};

export default ProductCard;